<script setup lang="ts">
import RadialbarCharts from '../../components/RadialbarCharts.vue'
import ViewAgencyDialog from '../../components/dialogs/ViewAgencyDailog.vue'
import TodoProxySourceFilter from '@/components/TodoProxySourceFilter.vue'
import { computed, ref, watch } from 'vue'
import { useSessionStorage, useStorage } from '@vueuse/core'
import { useTheme } from 'vuetify'
import {
  countProxyTodoSources,
  filterTodosByProxySource,
  isProxyTodoItem,
  proxyPrincipalLabel,
  type ProxyFilterMode,
} from '@/utils/todoListFilter'

const classCommon = ref('rounded-lg elevation-8')
definePage({
  meta: {
    action: 'manage',
    subject: 'root',
  },
})

//  Data table
const data = ref([])

const grid1Headers =ref([
  { title: '#', key:'id' , align: 'start'},
  { title: '工作內容', key: 'activityName' },
  { title: '送出人', key: 'creatorName' },
  { title: '送出時間', key: 'createTime' },
  { title: '操作', key: 'actions', sortable: false },
])

const compId = ref(useStorage('compNo').value)
const userOrgType = useStorage('orgType').value
const menuId = ref()
const menuType = ref('S')
const menuDesc = ref()
const currentPage = ref(1)
const size = ref(10)
const sortOrder = ref<string | undefined>('DESC')
const orderByCol = ref<string | undefined>('createTime')
const totalDatas = ref(0)
const sortBy = ref<{ key: string, order: 'asc' | 'desc' | undefined }[]>([{ key: 'createTime', order: 'desc' }])

watch(sortBy, (val) => {
  if (val && val.length > 0) {
    orderByCol.value = val[0].key
    sortOrder.value = val[0].order?.toUpperCase()
  } else {
    orderByCol.value = undefined
    sortOrder.value = undefined
  }
})

// dialog - indexDailog
const isDialogVisible = ref(false)
const propsData = ref({})

const openDailog = (item) =>{
  isDialogVisible.value=true
  propsData.value = item
  // console.log(propsData.value)
}
// 👉 Fetching users
// const { data: menusData, execute: fetchMenus } = await useApi<any>(createUrl('', {
//   query: {
//     compId,
//     menuId,
//     menuType,
//     menuDesc,
//     page,
//     size,
//     orderByCol,
//     sortOrder,
//   },
// }))
const { global } = useTheme()
const themeColors = computed(() => global.current.value.colors)

const getStatCardThemeVars = (color: string) => {
  const colors = themeColors.value
  const base = colors[color] ?? colors.primary
  const dark = colors[`${color}-darken-1`] ?? base

  return {
    '--stat-accent': base,
    '--stat-accent-dark': dark,
  }
}

const boxes = ref([
  {
    title:'憑證上傳',
    icon:'tabler-device-tablet-up',
    number:10,
    color:'error',
    subtitle:'已上傳的憑證',
    linkSubTitle:'查看憑證清單',
    linkOrgC:'/tca/erm/PaymentVoucherUpload',
    linkList:'/tca/erm/PaymentVoucherUpload',
    applyLink:'/tca/erm/PaymentVoucherUpload',
    applyTooltip:'上傳憑證',
  },
  {
    title:'黃金計畫',
    icon:'tabler-trophy',
    number:10,
    color:'info',
    subtitle:'進行中的黃金計畫',
    linkSubTitle:'查看黃金計畫清單',
    linkOrgC:'/tca/erm/ExpenseRequestCenterList',
    linkList:'/tca/erm/ExpenseRequestList',
    applyLink:'/tca/erm/GoldPlanApply',
    applyTooltip:'申請黃金計畫',
  },
  {
    title:'經費申請',
    icon:'tabler-writing',
    number:10,
    color:'warning',
    subtitle:'進行中的經費申請',
    linkSubTitle:'查看經費申請清單',
    linkOrgC:'/tca/erm/ExpenseRequestCenterList',
    linkList:'/tca/erm/ExpenseRequestList',
    applyLink:'/tca/erm/ExpenseRequest',
    applyTooltip:'申請經費',
  },
  {
    title:'經費核銷',
    icon:'tabler-moneybag',
    number:10,
    color:'primary',
    subtitle:'進行中的經費核銷',
    linkSubTitle:'查看經費核銷清單',
    linkOrgC:'/tca/erm/ExpenseReimbursementCenterList',
    linkList:'/tca/erm/ExpenseReimbursementList',
    applyLink:'/tca/erm/ExpenseReimbursement',
    applyTooltip:'核銷經費',
  },
])

/** TCA 首頁待辦（含本人／代理標示） */
const { data: workItemData, execute: fetchDepts } = await useApi<any>(createUrl('/tca/todo/list'))
const name = ['已送訓輔','中心備查','已公告']
const projectName = {
  project:'選手培訓參賽實施計畫',
  guide:'代表隊遴選辦法'
}
// 圓餅樹數據
const { data: fetchorkRate, execute: fetchRates } = await useApi<any>(createUrl('/tca/projectschedule/homeInfo', {
  query: {
  },
}))
const workRate = computed(()=>{
  if(fetchorkRate.value&&fetchorkRate.value?.data){
    // fetchorkRate.value.data.
    // console.log(fetchorkRate.value.data.guide.toArray())
    const transformedData = fetchorkRate.value.data.map(item => {
      const projectTransformed = {
          rate: Object.entries(item.project).map(([key, value]) => ({ [key]: value })),
          projectTitle: item.projectTitle+projectName.project
      };

      const guideTransformed = {
          rate: Object.entries(item.guide).map(([key, value]) => ({ [key]: value })),
          projectTitle: item.projectTitle+projectName.guide
      };

      return [projectTransformed, guideTransformed];
      }).flat();
    return transformedData
  }else{
    return []
  }
})

const allTodos = computed(() => {
  const raw = workItemData.value?.data
  return Array.isArray(raw) ? raw : []
})

const proxyFilterMode = ref<ProxyFilterMode>('ALL')
const proxyFilterCounts = computed(() => countProxyTodoSources(allTodos.value))

watch(
  () => proxyFilterCounts.value.PROXY,
  (proxyCount) => {
    if (proxyCount === 0 && proxyFilterMode.value !== 'ALL')
      proxyFilterMode.value = 'ALL'
  },
)

const filteredTodos = computed(() =>
  filterTodosByProxySource(allTodos.value, proxyFilterMode.value),
)

const datas = computed(() => {
  const from = (currentPage.value - 1) * size.value
  return filteredTodos.value.slice(from, from + size.value)
})

watch(filteredTodos, (rows) => {
  totalDatas.value = rows.length
  const maxPage = Math.max(1, Math.ceil(rows.length / size.value) || 1)
  if (currentPage.value > maxPage)
    currentPage.value = maxPage
}, { immediate: true })

watch(proxyFilterMode, () => {
  currentPage.value = 1
})

watch([orderByCol, sortOrder], () => {
  fetchDepts()
})

const router = useRouter()
const windowOrigin = window.location.origin
const isDev = import.meta.env.MODE === 'development'

/** 待辦連結可能是相對路徑或完整 URL，轉成 Vue Router 可用的 path／query */
const toSpaLocation = (raw: string): { path: string; query: Record<string, string> } | null => {
  const value = raw.trim()
  if (!value)
    return null

  let pathname = value
  let search = ''

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value)
      pathname = parsed.pathname
      search = parsed.search.startsWith('?') ? parsed.search.slice(1) : parsed.search
    }
    catch {
      return null
    }
  }
  else {
    const q = value.indexOf('?')
    if (q >= 0) {
      pathname = value.slice(0, q)
      search = value.slice(q + 1)
    }
    if (!pathname.startsWith('/'))
      pathname = `/${pathname}`
  }

  const query: Record<string, string> = {}
  if (search) {
    new URLSearchParams(search.split('#')[0]).forEach((v, k) => {
      query[k] = v
    })
  }

  return { path: pathname.split('#')[0], query }
}

const openWorkItem = (item) => {
  const raw = item?.keyFieldValue || item?.url
  if (!raw)
    return

  if (isDev || windowOrigin.includes('testnstc')) {
    const loc = toSpaLocation(String(raw))
    if (loc?.path)
      router.push({ path: loc.path, query: loc.query })
    else
      window.location.href = raw
    return
  }
  window.location.href = raw
}

const { data: submissionAsContactData} =
await useApi<any>(
  createUrl("/q/de/select/select/select/deSelect", {
    query: {
      tabId: "SubmissionAsContact",
    },
  })
);

useSessionStorage('SubmissionAsContact',submissionAsContactData.value[0].label)


</script>
<template>
  <div class="tca-home-page">
    <v-row class="home-stat-row">
      <v-col
        v-for="box in boxes"
        :key="box.title"
        cols="12"
        md="3"
        lg="3"
      >
        <VCard
          class="home-stat-card"
          :data-accent="box.color"
          :style="getStatCardThemeVars(box.color)"
        >
          <div class="home-stat-card__header">
            <span class="home-stat-card__title">{{ box.title }}</span>
          </div>

          <div class="home-stat-card__body">
            <div class="home-stat-card__main">
              <p class="home-stat-card__subtitle">
                {{ box.subtitle }}
              </p>

              <v-tooltip :text="box.applyTooltip">
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    :to="box.applyLink"
                    icon
                    size="64"
                    variant="flat"
                    class="home-stat-card__action-btn"
                  >
                    <VIcon :icon="box.icon" size="34" />
                  </VBtn>
                </template>
              </v-tooltip>
            </div>

            <RouterLink
              :to="userOrgType == 'C' ? box.linkOrgC : box.linkList"
              class="home-stat-card__footer"
            >
              <span class="home-stat-card__footer-text">{{ box.linkSubTitle }}</span>
              <VIcon
                icon="tabler-chevron-right"
                size="22"
                class="home-stat-card__footer-icon"
              />
            </RouterLink>
          </div>
        </VCard>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <VCard class="home-todo-card">
          <ListPageHeader
            embedded
            title="簽核待辦工作"
            subtitle="待您簽核處理的工作項目"
            icon="tabler-clipboard-check"
            :count="totalDatas"
          />

          <TodoProxySourceFilter
            v-model="proxyFilterMode"
            :counts="proxyFilterCounts"
          />

          <VDataTableServer
            v-model:items-per-page="size"
            v-model:page="currentPage"
            v-model:sort-by="sortBy"
            :items="datas || []"
            :items-length="totalDatas"
            :headers="grid1Headers"
            class="home-todo-table text-no-wrap"
            :no-data-text="proxyFilterMode === 'ALL' ? '尚無待辦工作' : '此篩選尚無待辦'"
          >
            <template #item.id="{ index }">
              <span class="home-todo-table__index">{{ index + 1 }}</span>
            </template>

            <template #item.activityName="{ item }">
              <div class="d-flex align-center flex-wrap ga-2">
                <span class="home-todo-table__activity">{{ item.activityName }}</span>
                <VChip
                  v-if="isProxyTodoItem(item)"
                  color="warning"
                  variant="tonal"
                  size="x-small"
                  class="rounded-pill font-weight-medium"
                  prepend-icon="tabler-user-share"
                >
                  代 {{ proxyPrincipalLabel(item) }}
                </VChip>
              </div>
            </template>

            <template #item.creatorName="{ item }">
              <span class="home-todo-table__creator">{{ item.creatorName }}</span>
            </template>

            <template #item.createTime="{ item }">
              <span class="home-todo-table__time">{{ item.createTime }}</span>
            </template>
            <template #item.actions="{ item }">
              <VBtn
                v-if="item.keyFieldValue"
                variant="tonal"
                color="primary"
                density="compact"
                prepend-icon="tabler-eye"
                class="home-todo-table__view-btn"
                @click="openWorkItem(item)"
              >
                檢視
              </VBtn>
            </template>
            <!-- pagination -->
            <template #bottom>
              <TablePagination
                v-model:page="currentPage"
                v-model:size="size"
                :items-per-page="size"
                :total-items="totalDatas"
              />
            </template>
          </VDataTableServer>
        </VCard>
      </v-col>
      <!-- <v-col cols="12" lg="5" md="12" sm="12" xs="12">
        <VCard class="h-100">
          <VCardItem class="py-3">
            <VCardTitle>經費核結情況</VCardTitle>
          </VCardItem>
          <VCardText>
            <VRow >
              <VCol cols="12" sm="4" v-for="i in 3" :key="i">
                <RadialbarCharts height="250" :series=[63.78] :circleTitle="'移訓核結率'+i" ></RadialbarCharts>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </v-col> -->
      <v-col
        v-if="userOrgType == 'C'"
        v-for="(workrate, index) in workRate"
        :key="index"
        cols="12"
        lg="6"
        md="12"
        sm="12"
        xs="12"
      >
        <VCard class="home-chart-card h-100">
          <VCardItem class="py-3">
            <VCardTitle>{{workrate.projectTitle}}</VCardTitle>
          </VCardItem>
          <VCardText>
            <VRow >
              <VCol cols="12" sm="4" v-for="(item,index) in workrate.rate" :key="index">
                <RadialbarCharts height="250" :series="[item[name[index]]]" :circleTitle="name[index]" ></RadialbarCharts>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </v-col>
    </v-row>
    <ViewAgencyDialog
      v-if="isDialogVisible"
      v-model:is-dialog-visible="isDialogVisible"
    />
      <!-- :props-data="propsData" -->
  </div>
</template>
<style scoped>

</style>
